import '@iotize/tap/auth';
import '@iotize/tap/service/impl/single-packet';

import { retryWithDelay } from '@iotize/common/rxjs';
import { StepOperations } from '@iotize/common/task-manager';
import { Tap } from '@iotize/tap';
import { CRC } from '@iotize/tap/client/impl';
import { ComProtocol } from '@iotize/tap/protocol/api';
import {
  SinglePacketStoreInfo,
  SinglePacketStoreLoader,
} from '@iotize/tap/service/impl/single-packet';
import { defer, empty, Observable, throwError } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { isCodeError } from '@iotize/common/error';
import { TapError } from '@iotize/tap';

export class TapNLinkUpdater extends StepOperations<
  TapNLinkUpdater.Event,
  Record<string, any>
> {
  static create(
    tap: Tap,
    firmwareUpdateData: Uint8Array,
    options: TapNLinkUpdater.Options = TapNLinkUpdater.DEFAULT_OPTIONS,
  ): TapNLinkUpdater {
    if (!options.loader) {
      options.loader = TapNLinkUpdater.DEFAULT_OPTIONS.loader;
    }
    const loadFirmwareUpdateOperations =
      SinglePacketStoreLoader.createOperationsFromBytes(
        firmwareUpdateData,
        tap.service.singlePacket,
        options.loader,
      );
    const expectedCRC = SinglePacketStoreLoader.computeCRC(firmwareUpdateData);
    const operations: StepOperations.OperationType<
      TapNLinkUpdater.Event,
      any
    >[] = [
      async (observer, context) => {
        observer.next({
          step: 'setup',
        });
        if (!tap.encryption.isStarted) {
          await tap.encryption.start();
        }
      },
      ...loadFirmwareUpdateOperations,
      // singlePacketStoreLoader
      //     .asObservable()
      //     .pipe(
      //         map<SinglePacketStoreLoader.Event, TapNLinkUpdater.Progress>(
      //             (progress: SinglePacketStoreLoader.Event, index: number) => {
      //                 return {
      //                     step: 'load',
      //                     payload: progress
      //                 };
      //             }
      //         )
      //     ),
      async (observer, context) => {
        try {
          observer.next({
            step: 'apply',
          });
          (
            await tap.service.singlePacket.executeSinglePacketInStore(
              expectedCRC,
            )
          ).successful();
          observer.next({
            step: 'finish',
            executeRequestTimeout: false,
          });
        } catch (err: any) {
          // Fix for old firmware that do not respond to execute command
          if (err?.cause?.code === ComProtocol.ErrorCode.TimeoutError) {
            observer.next({
              step: 'finish',
              executeRequestTimeout: true,
            });
            return;
          }
          throw err;
        }
      },
    ];
    if (options.reconnectAfterUpdate) {
      operations.push(TapNLinkUpdater.waitingForReconnection(tap, options));
    }

    return new TapNLinkUpdater(operations, options);
  }

  constructor(
    steps: StepOperations.OperationType<TapNLinkUpdater.Event>[],
    public readonly options: TapNLinkUpdater.Options = {},
  ) {
    super(StepOperations.createSequentialContainers(steps));
  }

  // public start(
  //     firmwareUpdateData: Uint8Array
  // ): Observable<TapNLinkUpdater.Progress> {

  //     // const singlePacketStoreLoader = tap.service.singlePacket
  //     //     .createStoreLoaderFromBytes(firmwareUpdateData);

  // }

  private async isFirmwareUpdateAvailable(tap: Tap): Promise<boolean> {
    const info = (await tap.service.singlePacket.getInfo()).body();
    return info.state === SinglePacketStoreInfo.State.EMPTY;
  }

  static waitingForReconnection(
    tap: Tap,
    options?: {
      reconnectRetryCount?: number;
      reconnectRetryDelay?: number;
    },
  ): Observable<TapNLinkUpdater.Event> {
    const maxRetry =
      options?.reconnectRetryCount === undefined
        ? TapNLinkUpdater.DEFAULT_OPTIONS.reconnectRetryCount
        : options.reconnectRetryCount;
    return retryWithDelay(
      defer(() => tap.connect()),
      {
        delay:
          options?.reconnectRetryDelay === undefined
            ? TapNLinkUpdater.DEFAULT_OPTIONS.reconnectRetryDelay
            : options.reconnectRetryDelay,
        retryCount: maxRetry,
      },
    ).pipe(
      map((v, index) => {
        return {
          step: 'restart',
          attempt: index + 1,
          maxRetry,
          message: `Tap reconnection attempt n°${index + 1}/${maxRetry + 1}`,
        };
      }),
    );
  }
}

export namespace TapNLinkUpdater {
  export const DEFAULT_OPTIONS: Required<Options> = {
    reconnectAfterUpdate: true,
    reconnectRetryCount: 5,
    reconnectRetryDelay: 5000,
    loader: {
      execute: false,
      maxRetry: 10,
      retryDelay: 1000,
    },
  };

  export interface Options {
    /**
     * True to try to reconnect tap after firmware update is done
     */
    reconnectAfterUpdate?: boolean;
    /**
     * Number retry for reconnection after firmware update is done
     */
    reconnectRetryCount?: number;
    /**
     * Delay between reconnection attempt in milliseconds
     */
    reconnectRetryDelay?: number;
    /**
     * Single packet store loader options
     */
    loader?: SinglePacketStoreLoader.Options;
  }

  interface RestartStepProgress {
    step: 'restart';
  }

  interface FinishStepProgress {
    step: 'finish';
    executeRequestTimeout?: boolean;
  }

  interface ApplyStepProgress {
    step: 'apply';
  }

  interface SetupStepProgress {
    step: 'setup';
  }

  // interface LoadStepProgress {
  //     step: 'load';
  //     payload: SinglePacketStoreLoader.Event;
  // }

  export type Event =
    | SinglePacketStoreLoader.Event
    | SetupStepProgress
    | ApplyStepProgress
    | RestartStepProgress
    | FinishStepProgress;
}
